<template>
  <div
    class="zm-select zm-select-fix"
    :class="[
      (selectSize ? 'zm-select--' + selectSize : ''),
      (widthLimit ? 'zm-select-width-limit' : ''),
      (visible || isInputFocus ? 'is-focus' : ''),
      (selectDisabled ? 'is-disabled': '')
    ]"
    @click.stop="handleSelectClick"
    v-clickoutside="handleClose">
    <zm-scrollbar
      class="zm-select-scrollbar-fix"
      :wrap-style="inputWrapStyl"
    >
      <div
        class="zm-select-input1"
        :class="{ 'has-slot': $slots.prefix }"
        ref="reference"
        @mouseenter="inputHovering = true"
        @mouseleave="inputHovering = false"
      >
        <div class="zm-select-prefix" v-if="$slots.prefix">
          <slot name="prefix"></slot>
        </div>
        <template v-if="filterable && !multiple">
          <input
            class="zm-select-input__inner1"
            autocomplete="off"
            ref="referenceInner"
            v-model="query"
            type="text"
            :placeholder="currentPlaceholder"
            :disabled="selectDisabled"
            :validate-event="false"
            @keydown="(e)=>{handleKeyDown(e,true)}"
            :aria-activedescendant="visible ? activedescendant: null"
            :aria-controls="_targetId+'-popup-list'"
            :aria-owns="_targetId+'-popup-list'"
            :aria-required="ariaRequired"
            :aria-label="label"
            :aria-describedby="labelBy"
            role="combobox"
            :aria-expanded="(visible).toString()"
            aria-haspopup="true"
            @compositionstart="handleComposition"
            @compositionupdate="handleComposition"
            @compositionend="handleComposition"
            @input="debouncedQueryChange"
            @focus="handleFocus"
            @blur="handleBlur"
          />
        </template>
        <span
          v-else
          :aria-activedescendant="visible ? activedescendant: null"
          :aria-controls="_targetId+'-popup-list'"
          :aria-owns="_targetId+'-popup-list'"
          :aria-required="ariaRequired"
          :aria-label="(label ? (label + ',') : '') + (selectedLabel || currentPlaceholder)"
          :aria-describedby="labelBy"
          class="zm-select-span__inner1"
          :class="{'is-placeholder': selectedLabel===''}"
          ref="referenceInner"
          :tabindex="selectDisabled || multiFilterable ? -1 : 0"
          :id="_targetId"
          :disabled="selectDisabled"
          role="button"
          :aria-expanded="(visible).toString()"
          aria-haspopup="listbox"
          @keydown="handleKeyDown"
          @focus="handleFocus"
          @blur="handleBlur"
          v-show="!multiFilterable"
        >
         {{selectedLabel || currentPlaceholder}}
      </span>
        <div
          class="zm-select__tags1"
          v-if="multiple"
          ref="tags"
          :style="{ 'max-width': inputWidth - 25 + 'px' }">
          <span v-if="collapseTags && selected.length">
            <zm-tag
              :closable="!selectDisabled"
              class="zm-tag-fix"
              :size="collapseTagSize"
              :hit="selected[0].hitState"
              :delete-text="t('tag.delete') + ' ' + selected[0].currentLabel"
              type="info"
              @close="deleteTag($event, selected[0])"
              disable-transitions>
            <span class="zm-select__tags-text">{{ selected[0].currentLabel }}
            </span>
            </zm-tag>
            <zm-tag
              class="zm-tag-fix"
              v-if="selected.length > 1"
              :closable="false"
              :size="collapseTagSize"
              type="info"
              disable-transitions>
            <span class="zm-select__tags-text">+ {{ selected.length - 1 }}
            </span>
            </zm-tag>
          </span>
          <transition-group @after-leave="resetInputHeight" v-if="!collapseTags">
            <zm-tag
              v-for="item in selected"
              :key="getValueKey(item)"
              :delete-text="t('tag.delete') + ' '+ item.currentLabel"
              :closable="!selectDisabled"
              :size="collapseTagSize"
              :hit="item.hitState"
              type="info"
              class="zm-tag-fix"
              @close="deleteTag($event, item)"
              disable-transitions>
              <span class="zm-select__tags-text">{{ item.currentLabel }}</span>
            </zm-tag>
          </transition-group>

          <input
            type="text"
            class="zm-select__input1"
            autocomplete="off"
            :aria-required="ariaRequired"
            :class="[selectSize ? `is-${ selectSize }` : '']"
            :disabled="selectDisabled"
            @click.stop
            @keydown.down.prevent="navigateOptions('next')"
            @keydown.up.prevent="navigateOptions('prev')"
            @keydown.home.stop.prevent="navigateOptions('home')"
            @keydown.end.stop.prevent="navigateOptions('end')"
            @keydown.enter.prevent="selectOption"
            @keydown.esc.prevent="handleEsc"
            @keydown.tab="handleTab"
            @keyup="managePlaceholder"
            @keydown="resetInputState"
            @keydown.delete="deletePrevTag"
            @compositionstart="handleComposition"
            @compositionupdate="handleComposition"
            @compositionend="handleComposition"
            v-model="query"
            @input="debouncedQueryChange"
            v-if="filterable"
            :aria-label="label"
            :aria-describedby="labelBy"
            :aria-activedescendant="visible ? activedescendant: null"
            :aria-controls="_targetId+'-popup-list'"
            :aria-owns="_targetId+'-popup-list'"
            role="combobox"
            :aria-expanded="(visible).toString()"
            aria-haspopup="true"
            :style="{ 'flex-grow': '1', width: inputLength / (inputWidth - 32) + '%', 'max-width': inputWidth - 42 + 'px' }"
            ref="input"
            @focus="handleFocus"
            @blur="handleBlur"
            :placeholder="currentPlaceholder"
          >
        </div>
        <button
          class="zm-select-clear"
          v-show="showClear"
          type="button"
          @click.stop="clearSelect"
          @keydonw.enter="clearSelect"
          aria-label="Clear Select"
        >
          <i :class="['zm-select__caret', 'zm-input__icon', 'zm-icon-error','is-show-close']"></i>
        </button>
        <button
          tabindex="-1"
          class="zm-select-toggle"
          type="button"
          aria-hidden="true"
          @click.stop="handleIconClick"
          :aria-label="toggleText">
          <i :class="['zm-select__caret', 'zm-input__icon', 'zm-icon-' + iconClass]"></i>
        </button>
      </div>
    </zm-scrollbar>
    <transition
      name="zm-zoom-in-top"
      @after-enter="handleMenuEnter"
      @after-leave="handleMenuLeave">
      <zm-select-dropdown
        ref="popper"
        :class="{'zm-select-menu-width-limit': widthLimit}"
        :append-to-body="popperAppendToBody"
        :width-limit="widthLimit"
        :popper-options="popperOptions"
        v-show="visible && emptyText!==false">
        <div v-if="tree" class="zm-select-dropdown__wrap">
          <zm-tree
            :data="tree.data"
            :default-expanded-keys="tree.defaultExpandedKeys"
            :default-checked-keys="value"
            show-checkbox
            highlight-current
            node-key="id"
            ref="tree"
            @hook:mounted="onTreeSelectChange"
            @check="onTreeSelectChange"
            @keydown="onDropDownKeyDown"
          >
          </zm-tree>
        </div>
        <zm-scrollbar
          v-else
          wrap-class="zm-select-dropdown__wrap"
          :wrap-style="wrapStyl"
          ref="scrollbar"
          :class="{ 'is-empty': hasEmptyCls }"
          @scroll-end="onScrollEnd"
        >
          <zm-checkbox-list
            v-if="localMultiple"
            v-model="checkListVal"
            :items="checkboxItems"
            :column-count="1"
            :span="24"
            role="application"
            :limit="multipleLimit"
            :id="listId"
            @change="onCheckListChange"
            ref="checkboxList"
            @keydown="onDropDownKeyDown"
          >
          </zm-checkbox-list>
          <dl
            role="listbox"
            tabindex="-1"
            :aria-label="listLabel"
            :aria-labelledby="listLabelBy"
            :id="listId"
            class="zm-select-dropdown__list"
            v-else
            v-show="shouldShowOptions"
          >
            <zm-option
              :value="query"
              created
              :option-id="query"
              :width-limit="widthLimit"
              v-if="showNewOption">
            </zm-option>
            <slot></slot>
          </dl>
          <div v-if="loading" v-loading="loading" class="zm-select__loading">
          </div>
        </zm-scrollbar>
        <template v-if="emptyText && (!allowCreate || (allowCreate && options.length === 0 ))">
          <slot name="empty" v-if="$slots.empty"></slot>
          <p class="zm-select-dropdown__empty" v-else>
            {{ emptyText }}
          </p>
        </template>
      </zm-select-dropdown>
    </transition>
  </div>
</template>

<script type="text/babel">
  import '../../../i18n/select';
  import Emitter from '../../mixins/emitter';
  import Locale from '../../mixins/locale';
  import ZmInput from '../input';
  import ZmSelectDropdown from './select-dropdown.vue';
  import ZmOption from './option.vue';
  import ZmTag from '../tag';
  import ZmScrollbar from '../scrollbar';
  import ZmCheckboxList from '../checkbox-list';
  import debounce from 'throttle-debounce/debounce';
  import Clickoutside from '../../utils/clickoutside';
  import { addClass, removeClass, hasClass } from '../../utils/dom';
  import { addResizeListener, removeResizeListener } from '../../utils/resize-event';
  import { t } from '../../locale';
  import scrollIntoView from '../../utils/scroll-into-view';
  import { getValueByPath } from '../../utils/util';
  import { valueEquals } from '../../utils/util';
  import NavigationMixin from './navigation-mixin';
  import { isKorean } from '../../utils/shared';
  import AriaUtils from '../../utils/aria-utils';
  import { arrayFind } from '../../utils/util';
  import { readAria } from '../../utils/aria';
  import scrollbarWidth from '../../utils/scrollbar-width';

  const sizeMap = {
    'large': 38, // 加上2像素的边框厚度后为40px
    'small': 30
  };

  let _uid = 0;

  function uid() {
    return _uid++;
  }

  export default {
    mixins: [Emitter, Locale, NavigationMixin],

    name: 'ZmSelect',

    componentName: 'ZmSelect',

    inject: {
      ZmForm: {
        default: ''
      },

      ZmFormItem: {
        default: ''
      }
    },

    provide() {
      return {
        'select': this
      };
    },

    computed: {
      listId() {
        return `${this._targetId}-popup-list`;
      },

      _targetId() {
        return this.id ? this.id : 'select-' + uid();
      },
      _elFormItemSize() {
        return (this.ZmFormItem || {}).elFormItemSize;
      },

      multiFilterable() {
        return this.multiple && this.filterable;
      },

      shouldShowOptions() {
        return this.options.length > 0 && (!this.loading || (this.loading && this.hasMore))
      },

      hasEmptyCls() {
        if (this.allowCreate || this.loading) {
          return false
        }
        if (this.filterable) {
          return this.filteredOptionsCount === 0
        } else {
          if (this.multiple) {
            return this.checkboxOptions.length === 0
          } else {
            return this.optionsCount === 0
          }
        }
      },

      wrapStyl() {
        const gutter = scrollbarWidth();
        const maxHeight = gutter ? '239px' : '224px';
        return `max-height:${maxHeight};`;
      },

      iconClass() {
        return this.remote && this.filterable ? '' : 'up';
      },

      showClear() {
        return this.clearable &&
          !this.selectDisabled &&
          !this.multiple &&
          this.value !== undefined &&
          this.value !== null &&
          this.value !== '' && (
            !this.filterable || !this.visible
          );
      },

      debounce() {
        return this.remote ? 300 : 0;
      },

      toggleText() {
        if (this.visible) {
          return this.t('select.hideOptions');
        } else {
          return this.t('select.showOptions');
        }
      },
      emptyText() {
        if (this.loading) {
          return '';
        }
        if (this.remote && this.query === '' && this.options.length === 0) {
          return false;
        }
        if (this.filterable && this.query && this.options.length > 0 && this.filteredOptionsCount === 0) {
          return this.noMatchText || this.t('select.noMatch');
        }
        if (this.options.length === 0 && !this.tree && !this.localMultiple) {
          return this.noDataText || this.t('select.noData');
        }
        return null;
      },

      showNewOption() {
        const query = typeof this.query === 'string' ? this.query.trim() : String(this.query)
        let hasExistingOption = this.options.filter(option => !option.created)
          .some(option => option.currentLabel === query);
        return this.filterable && this.allowCreate && query !== '' && !hasExistingOption;
      },

      selectSize() {
        return this.size || this._elFormItemSize
      },

      selectDisabled() {
        return this.disabled || (this.ZmForm || {}).disabled;
      },

      collapseTagSize() {
        return ['small'].indexOf(this.selectSize) > -1
          ? 'small'
          : 'middle';
      },

      checkboxItems() {
        const items = [];
        this.checkboxOptions.forEach((option) => {
          items.push({
            label: option.value,
            displayName: option.label,
            disabled: option.disabled,
            ariaLabel: option.ariaLabel
          });
        });
        return items;
      },

      localMultiple() {
        return this.multiple && !this.remote && !this.filterable;
      }
    },

    components: {
      ZmInput,
      ZmSelectDropdown,
      ZmOption,
      ZmTag,
      ZmScrollbar,
      ZmCheckboxList,
      ZmTree: () => import(/* webpackChunkName: 'zm-tree' */ '../tree')
    },

    directives: { Clickoutside },

    props: {
      id: {
        type: String,
        default: ''
      },
      value: {
        required: true
      },
      ariaRequired: {
        type: Boolean,
        default: false
      },
      size: {
        type: String,
        default: 'small'
      },
      disabled: Boolean,
      clearable: Boolean,
      filterable: Boolean,
      allowCreate: Boolean,
      loading: Boolean,
      hasMore: Boolean,
      remote: Boolean,
      loadingText: String,
      noMatchText: String,
      noDataText: String,
      remoteMethod: Function,
      filterMethod: Function,
      widthLimit: [Boolean, Object],
      multiple: Boolean,
      multipleLimit: {
        type: Number,
        default: 0
      },
      placeholder: {
        type: String,
        default() {
          return t('select.placeholder');
        }
      },
      defaultFirstOption: Boolean,
      valueKey: {
        type: String,
        default: 'value'
      },
      collapseTags: Boolean,
      popperAppendToBody: {
        type: Boolean,
        default: true
      },
      listLabel: String,
      listLabelBy: String,
      label: String,
      labelBy: String,
      popperOptions: {
        default() {
          return {
            gpuAcceleration: false
          };
        }
      },
      popperClass: String,
      tree: {
        type: Object,
        default() {
          return null;
        }
      },
      checkboxOptions: {
        type: Array,
        default() {
          return [];
        }
      },
      selectOptions: {
        type: Array,
        default() {
          return [];
        }
      },
      selectOptionsValueKey: {
        type: String,
        default: 'value'
      },
      limitQueryLength: {
        type: Number,
        default: 0
      },
      filterLabel: Function,
      queryWhenFocus: Boolean,
      queryWhenVisible: {
        type: Boolean,
        default: true
      },
      selectedAfterFocus: Boolean,
      maxLine: {
        type: Number,
        default: 0
      },
      clearQueryWhenVisible: {
        type: Boolean,
        default: true
      },
      queryAfterValueChange: Boolean
    },

    data() {
      return {
        options: [],
        cachedOptions: [],
        createdLabel: null,
        createdSelected: false,
        selected: this.multiple ? [] : {},
        inputWidth: 0,
        inputLength: 20,
        // initialInputHeight: 0,
        cachedPlaceHolder: '',
        optionsCount: 0,
        filteredOptionsCount: 0,
        visible: false,
        // softFocus: false,
        selectedLabel: '',
        hoverIndex: -1,
        query: '',
        previousQuery: null,
        inputHovering: false,
        currentPlaceholder: '',
        // menuVisibleOnFocus: false,
        isOnComposition: false,
        isSilentBlur: false,
        activedescendant: '',
        isInputFocus: false,
        firstFocusElm: null,
        checkListVal: this.value,
        sortingOptions: false,
        firstQuery: true,
        page: 1,
        inputWrapStyl: '',
        visibleByQuery: false
      };
    },

    watch: {
      selectDisabled() {
        this.$nextTick(() => {
          this.resetInputHeight();
        });
      },

      placeholder(val) {
        this.cachedPlaceHolder = this.currentPlaceholder = val;
      },

      value(val, oldVal) {
        if (this.multiple) {
          this.resetInputHeight();
          if (val.length > 0 || (this.$refs.input && this.query !== '')) {
            this.currentPlaceholder = '';
          } else {
            this.currentPlaceholder = this.cachedPlaceHolder;
          }
          if (this.tree) {
            this.$refs.tree && this.$refs.tree.setCheckedKeys(val);
          } else {
            this.checkListVal = val;
          }
        }
        if (this.filterable && this.multiple) {
          this.$nextTick(() => {
            this.query = '';
            if (this.queryAfterValueChange) {
              this.handleQueryChange(this.query);
            }
          });
        }
        this.setSelected();
        if (this.filterable && !this.multiple) {
          this.query = this.selectedLabel;
        }
        if (this.filterable && !this.multiple) {
          this.inputLength = 20;
        }
        if (!valueEquals(val, oldVal)) {
          this.dispatch('ZmFormItem', 'el.form.change', val);
        }
      },

      visible(val) {
        this.resetHoverIndex();
        if (!val) {
          this.handleIconHide();
          this.broadcast('ZmSelectDropdown', 'destroyPopper');
          this.inputLength = 20;
          this.query = '';
          this.previousQuery = null;
          this.visibleByQuery = false;
          this.$nextTick(() => {
            if (this.$refs.input &&
              this.$refs.input.value === '' &&
              this.selected.length === 0) {
              this.currentPlaceholder = this.cachedPlaceHolder;
            }
            this.resetInputHeight();
          });

          if (!this.multiple) {
            if (this.selected) {
              if (this.filterable && this.allowCreate &&
                this.createdSelected && this.createdLabel) {
                this.selectedLabel = this.createdLabel;
              } else {
                this.selectedLabel = this.selected.currentLabel;
              }
              if (this.filterable) {
                this.query = this.selectedLabel;
                // this.$nextTick(() => {
                //   this.handleQueryChange(this.query);
                // });
              }
            }

            if (this.filterable) {
              this.currentPlaceholder = this.cachedPlaceHolder;
            }
          } else {
            this.$emit('blur');
            this.dispatch('ZmFormItem', 'el.form.blur');
          }
        } else {
          this.handleIconShow();
          this.broadcast('ZmSelectDropdown', 'updatePopper');
          if (this.filterable) {
            if (this.clearQueryWhenVisible && !this.visibleByQuery) {
              this.query = '';
            }
            if (this.queryWhenVisible) {
              this.handleQueryChange(this.query);
            }
            if (!this.remote) {
              if (this.selectedAfterFocus && !this.clearQueryWhenVisible) {
                const targetOptions = this.options.filter(option => option.currentValue === this.value);
                if (targetOptions.length) {
                  this.query = targetOptions[0].currentLabel;
                }
              }
            }
            this.isInputFocus = true;
            this.$nextTick(() => {
              this.focus();
              if (this.selectedAfterFocus) {
                (this.$refs.input || this.$refs.referenceInner).select();
              }
            });
          }
          this.firstQuery = true;
          this.$nextTick(() => {
            this.resetInputHeight();
          })
        }
        this.$emit('visible-change', val);
      },

      selectOptions() {
        this.sortingOptions = true;
        this.$nextTick(() => {
          this.sortOptions();
          this.sortingOptions = false;
        })
      },

      options() {
        if (this.$isServer) return;
        if (this.selectOptions && this.sortingOptions) {
          return;
        }
        this.$nextTick(() => {
          this.broadcast('ZmSelectDropdown', 'updatePopper');
        });
        this.resetHoverIndex();
        if (this.multiple) {
          this.resetInputHeight();
        }
        // let inputs = this.$el.querySelectorAll('input');
        // if ([].indexOf.call(inputs, document.activeElement) === -1) {
        //   this.setSelected();
        // }
        // TODO need setSelect?
        this.setSelected();
        if (this.filterable && !this.visible && !this.multiple && this.selectedLabel) {
          this.query = this.selectedLabel;
        }
        if (this.defaultFirstOption && (this.filterable || this.remote) && this.filteredOptionsCount) {
          this.checkDefaultFirstOption();
        }
      },

      emptyText(text) {
        if (text && !this.loading) {
          this.$nextTick(() => {
            if (this.emptyText) {
              readAria(this.emptyText);
            }
          });
        }
      }
    },

    methods: {
      handleSelectClick() {
        this.toggleMenu();
      },
      onScrollEnd() {
        if (!this.loading && this.hasMore) {
          this.page++;
          if (typeof this.remoteMethod === 'function') {
            this.remoteMethod(this.query, this.page);
          }
        }
      },
      sortOptions() {
        const options = [];
        this.selectOptions.forEach((option) => {
          options.push(arrayFind(this.options, (o) => {
            return o.value === option[this.selectOptionsValueKey];
          }));
        });
        this.options = options;
      },
      handleComposition(event) {
        const text = event.target.value;
        if (event.type === 'compositionend') {
          this.isOnComposition = false;
          this.handleQueryChange(text);
        } else {
          const lastCharacter = text[text.length - 1] || '';
          this.isOnComposition = !isKorean(lastCharacter);
        }
      },
      handleQueryChange(val, userInput) {
        this.firstQuery = false;
        this.page = 1;
        if (this.filterable && userInput && !this.visible && val) {
          this.visibleByQuery = true;
          this.visible = true;
        }
        if (this.remote && typeof this.remoteMethod === 'function') {
          if (this.query.length < this.limitQueryLength) {
            return;
          }
        }
        if (this.previousQuery === val || this.isOnComposition) {
          return;
        }
        this.previousQuery = val;
        this.$nextTick(() => {
          if (this.visible) this.broadcast('ZmSelectDropdown', 'updatePopper');
        });
        this.hoverIndex = -1;
        if (this.multiple && this.filterable) {
          const length = this.$refs.input.value.length * 15 + 20;
          this.inputLength = this.collapseTags ? Math.min(50, length) : length;
          this.managePlaceholder();
          this.resetInputHeight();
        }
        if (this.remote && typeof this.remoteMethod === 'function') {
          this.hoverIndex = -1;
          this.remoteMethod(val, this.page);
        } else if (typeof this.filterMethod === 'function') {
          this.filterMethod(val);
          this.broadcast('ZmOptionGroup', 'queryChange');
        } else {
          this.filteredOptionsCount = this.optionsCount;
          this.broadcast('ZmOption', 'queryChange', val);
          this.broadcast('ZmOptionGroup', 'queryChange');
        }
        if (this.defaultFirstOption && (this.filterable || this.remote) && this.filteredOptionsCount) {
          this.checkDefaultFirstOption();
        }
      },

      handleIconHide() {
        let icon = this.$el.querySelector('.zm-input__icon');
        if (icon) {
          removeClass(icon, 'is-reverse');
        }
      },

      handleIconShow() {
        let icon = this.$el.querySelector('.zm-input__icon');
        if (icon && !hasClass(icon, 'zm-icon-outline-error')) {
          addClass(icon, 'is-reverse');
        }
      },

      scrollToOption(option) {
        const target = Array.isArray(option) && option[0] ? option[0].$el : option.$el;
        if (this.$refs.popper && target) {
          const menu = this.$refs.popper.$el.querySelector('.zm-select-dropdown__wrap');
          scrollIntoView(menu, target);
        }
        this.$refs.scrollbar && this.$refs.scrollbar.handleScroll();
      },

      scrollToOptionMiddle(option) {
        const target = Array.isArray(option) && option[0] ? option[0].$el : option.$el;
        if (this.$refs.popper && target) {
          this.$refs.scrollbar && this.$refs.scrollbar.scrollIntoView(target, null, 0, -24);
        }
      },

      handleMenuEnter() {
        this.$nextTick(() => {
          this.scrollToOptionMiddle(this.selected);
        });
      },

      handleMenuLeave() {
        this.hoverIndex = -1;
        if (this.filterable) {
          return;
        }
      },

      handleKeyDown(event, isInput) {
        if (this.selectDisabled) {
          return;
        }
        const keyCode = event.keyCode;
        const keys = AriaUtils.keys;
        switch (keyCode) {
          case keys.enter:
            this.selectOption();
            event.preventDefault();
            event.stopPropagation();
            break;
          case keys.space:
            if (!isInput) {
              this.selectOption();
              event.preventDefault();
              event.stopPropagation();
            }
            break;
          case keys.esc:
            this.handleEsc();
            break;
          case keys.down:
            if (this.localMultiple) {
              if (this.visible) {
                if (this.tree) {
                  const treeItems = this.$refs.tree.$el.querySelectorAll('.is-focusable[role=treeitem]')
                  treeItems[0] && treeItems[0].focus();
                } else {
                  const checkboxList = this.$refs.checkboxList.$el;
                  checkboxList && checkboxList.focus();
                }
              } else {
                this.visible = true;
              }
            } else {
              this.navigateOptions('next');
            }
            event.preventDefault();
            event.stopPropagation();
            break;
          case keys.up:
            this.navigateOptions('prev');
            event.preventDefault();
            event.stopPropagation();
            break;
          case keys.home:
            this.navigateOptions('home');
            event.preventDefault();
            event.stopPropagation();
            break;
          case keys.end:
            this.navigateOptions('end');
            event.preventDefault();
            event.stopPropagation();
            break;
          case keys.tab:
            this.handleTab(event);
            break;
        }
      },

      onDropDownKeyDown(event) {
        const keyCode = event.keyCode;
        const keys = AriaUtils.keys;

        switch (keyCode) {
          case keys.esc:
            this.handleEsc();
            break;
          case keys.tab:
            this.visible = false;
            event.preventDefault();
            this.$nextTick(() => {
              this.focus();
            });
            break;
        }
      },

      emitChange(val) {
        if (!valueEquals(this.value, val)) {
          this.$emit('change', val);
        }
      },

      getOption(value) {
        let option;
        const isObject = Object.prototype.toString.call(value).toLowerCase() === '[object object]';
        const isNull = Object.prototype.toString.call(value).toLowerCase() === '[object null]';

        for (let i = this.cachedOptions.length - 1; i >= 0; i--) {
          const cachedOption = this.cachedOptions[i];
          const isEqual = isObject
            ? getValueByPath(cachedOption.value, this.valueKey) === getValueByPath(value, this.valueKey)
            : cachedOption.value === value;
          if (isEqual) {
            option = cachedOption;
            break;
          }
        }
        if (option) return option;
        const label = (!isObject && !isNull)
          ? value : '';
        let newOption = {
          value: value,
          currentLabel: label
        };
        if (this.multiple) {
          newOption.hitState = false;
        }
        return newOption;
      },

      setSelected() {
        if (!this.multiple) {
          let option = this.getOption(this.value);
          if (option.created) {
            this.createdLabel = option.currentLabel;
            this.createdSelected = true;
          } else {
            this.createdSelected = false;
          }
          this.selectedLabel = typeof this.filterLabel === 'function' ? this.filterLabel(option.currentLabel, this.value) : option.currentLabel;
          this.selected = option;
          // if (this.filterable) {
          //   this.query = this.selectedLabel;
          // }
          return;
        }
        const result = [];
        if (Array.isArray(this.value)) {
          if (this.tree && this.$refs.tree) {
            const nodes = this.$refs.tree.getCheckedNodes(false, false, true);
            nodes.forEach((node) => {
              result.push({
                value: node.id,
                currentLabel: node.label
              })
            })
          } else {
            this.value.forEach(value => {
              const val = this.localMultiple
                ? { value, currentLabel: this.getLabelByVal(value) }
                : this.getOption(value);
              result.push(val);
            });
          }
        }
        this.selected = result;
        this.$nextTick(() => {
          this.resetInputHeight();
        });
      },

      handleFocus(event) {
        this.isInputFocus = true;
        this.$emit('focus', event);
        this.$nextTick(() => {
          this.resetInputHeight();
          if (this.filterable && !this.visible) {
            // this.query = this.multiple ? '' : this.selectedLabel;
            if (this.queryWhenFocus) {
              this.handleQueryChange(this.query, true);
            }
          }
        });
      },
      focus() {
        const input = this.$refs.input || this.$refs.referenceInner;
        if (input) {
          input.focus();
        }
      },
      blur() {
        const input = this.$refs.input || this.$refs.referenceInner;
        if (input) {
          input.blur();
        }
      },

      handleBlur(event) {
        this.isInputFocus = false;
        this.$nextTick(() => {
          if (this.isSilentBlur) {
            this.isSilentBlur = false;
          } else {
            this.$emit('blur', event);
            this.dispatch('ZmFormItem', 'el.form.blur');
          }
          if (this.filterable && !this.multiple && !this.query) {
            this.query = this.selectedLabel;
          }
          this.resetInputHeight();
        });
      },

      handleIconClick() {
        if (this.selectDisabled) {
          return;
        }
        this.visible = !this.visible;
        if (this.visible) {
          this.focus();
        }
      },
      clearSelect(e) {
        this.deleteSelected(e);
        this.focus();
      },
      handleEsc() {
        this.visible = false;
        this.$nextTick(() => {
          this.focus();
        });
        // Trigger blur event to set active element on input, because the dialog will not be closed after esc if there are selects in dialog
        // this.$refs.referenceInner.blur();
      },
      handleTab(event) {
        if (!event.shiftKey) {
          this.selectOption();
        }
        this.visible = false;
      },
      doDestroy() {
        this.$refs.popper && this.$refs.popper.doDestroy();
      },

      handleClose() {
        this.visible = false;
      },

      // toggleLastOptionHitState(hit) {
      //   if (!Array.isArray(this.selected)) return;
      //   const option = this.selected[this.selected.length - 1];
      //   if (!option) return;
      //
      //   if (hit === true || hit === false) {
      //     option.hitState = hit;
      //     return hit;
      //   }
      //
      //   option.hitState = !option.hitState;
      //   return option.hitState;
      // },

      deletePrevTag(e) {
        if (e.target.value.length <= 0) {
          const value = this.value.slice();
          value.pop();
          this.$emit('input', value);
          this.emitChange(value);
        }
      },

      managePlaceholder() {
        if (this.currentPlaceholder !== '') {
          this.currentPlaceholder = this.$refs.input.value ? '' : this.cachedPlaceHolder;
        }
      },

      resetInputState(e) {
        // if (e.keyCode !== AriaUtils.keys.backspace) {
        //   this.toggleLastOptionHitState(false);
        // }
        this.inputLength = this.$refs.input.value.length * 15 + 20;
        this.resetInputHeight();
      },

      resetInputHeight() {
        if (!this.multiple) {
          return;
        }
        this.$nextTick(() => {
          if (!this.$refs.reference) {
            return;
          }
          // let inputChildNodes = this.$refs.reference;
          let input = this.$refs.referenceInner;
          const sizeInMap = sizeMap[this.selectSize] || 30;
          input.style.height = this.selected.length === 0
            ? sizeInMap + 'px'
            : 0;
          if (this.visible) {
            this.broadcast('ZmSelectDropdown', 'updatePopper');
          }
        });
      },

      resetHoverIndex() {
        if (!this.multiple) {
          this.hoverIndex = this.options.indexOf(this.selected);
        } else {
          if (this.selected.length > 0) {
            this.hoverIndex = Math.min.apply(null, this.selected.map(item => this.options.indexOf(item)));
          } else {
            this.hoverIndex = -1;
          }
        }
      },

      handleOptionSelect(option, byClick) {
        if (option.computedDisabled) {
          return;
        }
        this.firstFocusElm = option.$refs.optionLi;
        if (this.multiple) {
          const value = this.value.slice();
          const optionIndex = this.getValueIndex(value, option.value);
          if (optionIndex > -1) {
            value.splice(optionIndex, 1);
          } else if (this.multipleLimit <= 0 || value.length < this.multipleLimit) {
            value.push(option.value);
          }
          this.$emit('input', value);
          this.emitChange(value);
          if (option.created) {
            this.query = '';
            this.inputLength = 20;
          }
        } else {
          this.$emit('input', option.value);
          this.emitChange(option.value);
          this.visible = false;
        }
        if (option.optionId) {
          this.activedescendant = option.optionId;
        } else {
          this.activedescendant = option.value;
        }
        this.isSilentBlur = false;
        // if (this.visible) return;
        this.$nextTick(() => {
          this.scrollToOption(option);
          this.focus();
        });
      },

      handleOptionMouseDown() {
        this.isSilentBlur = true
      },

      getValueIndex(arr = [], value) {
        const isObject = Object.prototype.toString.call(value).toLowerCase() === '[object object]';
        if (!isObject) {
          return arr.indexOf(value);
        } else {
          const valueKey = this.valueKey;
          let index = -1;
          arr.some((item, i) => {
            if (getValueByPath(item, valueKey) === getValueByPath(value, valueKey)) {
              index = i;
              return true;
            }
            return false;
          });
          return index;
        }
      },

      toggleMenu() {
        if (!this.selectDisabled) {
          this.visible = true;
        }
      },

      selectOption() {
        if (!this.visible) {
          this.toggleMenu();
        } else {
          if (this.options[this.hoverIndex]) {
            this.handleOptionSelect(this.options[this.hoverIndex]);
          }
        }
      },

      deleteSelected(event) {
        event.stopPropagation();
        this.$emit('input', '');
        this.emitChange('');
        this.visible = false;
        this.$emit('clear');
      },

      deleteTag(event, tag) {
        let index = this.selected.indexOf(tag);
        if (index > -1 && !this.selectDisabled) {
          const value = this.value.slice();
          value.splice(index, 1);
          this.$emit('input', value);
          this.emitChange(value);
          this.$emit('remove-tag', tag.value);
          if (this.tree) {
            this.$refs.tree.setCheckedKeys(value.map((v) => {
              return v.value;
            }));
          }
          if (this.filterable) {
            this.focus();
          }
        }
        event.stopPropagation();
      },

      onOptionDestroy(index) {
        if (index > -1) {
          this.optionsCount--;
          this.filteredOptionsCount--;
          this.options.splice(index, 1);
        }
      },

      resetInputWidth() {
        this.inputWidth = this.$refs.reference.getBoundingClientRect().width;
      },

      handleResize() {
        this.resetInputWidth();
        if (this.multiple) this.resetInputHeight();
      },

      checkDefaultFirstOption() {
        this.hoverIndex = -1;
        // highlight the created option
        this.$nextTick(() => {
          let hasCreated = false;
          for (let i = this.options.length - 1; i >= 0; i--) {
            if (this.options[i].created) {
              hasCreated = true;
              this.hoverIndex = i;
              break;
            }
          }
          if (hasCreated) return;
          for (let i = 0; i !== this.options.length; ++i) {
            const option = this.options[i];
            if (this.query) {
              // highlight first options that passes the filter
              if (!option.computedDisabled && !option.groupDisabled && option.visible) {
                this.hoverIndex = i;
                break;
              }
            } else {
              // highlight currently selected option
              if (option.itemSelected) {
                this.hoverIndex = i;
                break;
              }
            }
          }
        });
      },

      getValueKey(item) {
        if (Object.prototype.toString.call(item.value).toLowerCase() !== '[object object]') {
          return item.value;
        } else {
          return getValueByPath(item.value, this.valueKey);
        }
      },

      onTreeSelectChange() {
        const nodes = this.$refs.tree.getCheckedNodes(false, false, true);
        const result = [];
        nodes.forEach(node => {
          result.push(node.id);
        });
        this.$emit('input', result);
        this.emitChange(result);
      },

      onCheckListChange(list) {
        this.$emit('input', list);
        this.emitChange(list);
      },

      getLabelByVal(val) {
        for (let i = 0; i < this.checkboxOptions.length; i++) {
          const option = this.checkboxOptions[i]
          if (option.value === val) {
            return option.label
          }
        }
      },

      setInputWrapStyl() {
        let style = 'height:auto;';
        if (this.maxLine > 0) {
          style += `max-height:${(this.maxLine * 30)}px;`;
        }

        this.inputWrapStyl = style;
      }
    },

    created() {
      this.cachedPlaceHolder = this.currentPlaceholder = this.placeholder;
      if (this.multiple && !Array.isArray(this.value)) {
        this.$emit('input', []);
      }
      if (!this.multiple && Array.isArray(this.value)) {
        this.$emit('input', '');
      }

      this.debouncedQueryChange = debounce(this.debounce, (e) => {
        this.handleQueryChange(e.target.value, true);
      });

      this.$on('handleOptionClick', this.handleOptionSelect);
      this.$on('setSelected', this.setSelected);
      this.$on('handleOptionMouseDown', this.handleOptionMouseDown)
    },

    mounted() {
      if (this.multiple && Array.isArray(this.value) && this.value.length > 0) {
        this.currentPlaceholder = '';
      }
      addResizeListener(this.$el, this.handleResize);

      const reference = this.$refs.reference;
      // if (reference) {
      //   this.initialInputHeight = reference.getBoundingClientRect().height;
      // }
      if (this.remote && this.multiple) {
        this.resetInputHeight();
      }
      this.$nextTick(() => {
        if (reference) {
          this.inputWidth = reference.getBoundingClientRect().width;
        }
      });
      this.setSelected();
      if (this.filterable && !this.multiple) {
        this.query = this.selectedLabel;
      }
      if (this.value) {
        this.activedescendant = this.value;
      }

      this.setInputWrapStyl();
    },

    beforeDestroy() {
      if (this.$el && this.handleResize) removeResizeListener(this.$el, this.handleResize);
      this.handleMenuLeave();
    }
  };
</script>

<style lang="scss">
  @import "../../scss/mixins/mixins";
  @import "../../scss/common/var";
  @import "../../scss/common/transition";
  @import "../../scss/mixins/utils";

  @include b(select) {
    display: inline-block;
    position: relative;
    .zm-select-input1.has-slot {
      display: flex;
      align-items: center;
      .zm-select-input__inner1 {
        flex: 1;
        width: auto;
      }
      .zm-select-span__inner1 {
        flex: 1;
        width: auto;
      }
    }
    .zm-select-input1 {
      position: relative;
      display: block;
      align-items: center;
      overflow-y: auto;
      overflow-x: hidden;
      border: none;
      /*line-height: $--input-height - 2;*/
      font-size: $--font-size-base;

      padding: $--input-default-padding-vertical $--input-default-padding-horizontal;
      padding-right: $--input-default-padding-horizontal + 13;

      .zm-select-prefix {
        display: block;
        line-height: 19px;
      }
    }
    .zm-select-input__inner1 {
      display: inline-block;
      resize: vertical;
      border: none;
      border-radius: $--input-border-radius;
      vertical-align: middle;
      -ms-overflow-style: none;
      width: 100%;
      line-height: $--input-height - 2;
      height: $--input-height - 2;
      max-width: 100%;
      min-width: 60px;
      padding-top: 0;
      padding-bottom: 0;
      color: $--input-color;
      font-size: $--font-size-base;
      background-color: transparent;
      background-image: none;

      &::placeholder {
        color: $--input-placeholder-color;
      }
      &:focus {
        outline: none;
      }
      &::-ms-clear {
        display: none;
      }
    }
    .zm-select-span__inner1 {
      display: block;
      vertical-align: middle;
      -ms-overflow-style: none;
      border: none;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      border-radius: $--input-border-radius;
      width: 100%;
      line-height: $--input-height - 2;
      height: $--input-height - 2;
      max-width: 100%;
      min-width: 60px;
      padding-top: 0;
      padding-bottom: 0;
      padding-left: 2px;
      color: $--input-color;
      background-color: transparent;
      background-image: none;
      scrollbar-width: none;
      &::-webkit-scrollbar {
        width: 0;
        height: 0;
      }

      &:focus {
        outline: none;
      }
      &::-ms-clear {
        display: none;
      }
      &.is-placeholder {
        color: $--color-text-placeholder;
      }
    }
    .zm-select-input1 {
      [disabled] + .zm-select-toggle {
        cursor: not-allowed;
        color: $--color-text-disabled;
        .zm-select__caret {
          color: $--color-text-disabled;
        }
        &:focus {
          color: $--color-text-disabled;
          background-color: transparent;
        }
        &:hover .zm-select__caret {
          color: $--color-text-disabled;
        }
      }
      .zm-select-toggle {
        position: absolute;
        right: 8px;
        top: 50%;
        margin-top: -8px;
        padding: 0;
        line-height: 14px;
        border-radius: 50%;
        background-color: transparent;
        border: none;
        .zm-button__slot {
          line-height: 11px;
        }
        &:focus {
          outline: none;
          color: $--color-text-primary-hover;
          background-color: $--color-background-secondary-active;
          .zm-select__caret {
            color: inherit;
          }
        }
        &:hover {
          background-color: transparent;
          border-right-color: transparent;
        }
      }
      .zm-select-clear {
        position: absolute;
        right: 24px;
        top: 50%;
        margin-top: -8px;
        padding: 0;
        line-height: 14px;
        border-radius: 50%;
        background-color: transparent;
        border: none;
      }
      .zm-select__caret {
        text-align: center;
        color: $--select-input-color;
        font-size: $--font-size-base;
        width: 16px;
        height: 16px;
        line-height: 16px;
        transition: transform .3s;
        transform: rotateZ(180deg);

        @include when(reverse) {
          transform: rotateZ(0deg);
        }

        @include when(show-close) {
          font-size: $--select-font-size;
          text-align: center;
          transform: rotateZ(180deg);
          border-radius: $--border-radius-circle;
          color: $--select-input-color;
          transition: $--color-transition-base;

          &:hover {
            color: $--select-close-hover-color;
          }
        }
      }

    }

    .zm-select-scrollbar-fix {
      border-radius: 8px;
    }

    @include e(input1) {
      border: none;
      outline: none;
      padding: 0;
      margin-left: 6px;
      color: $--select-multiple-input-color;
      font-size: $--select-font-size;
      appearance: none;
      height: 30px;
      min-width: 20px;
      background-color: transparent;
    }

    @include e(close) {
      cursor: pointer;
      position: absolute;
      top: 8px;
      z-index: $--index-top;
      right: 25px;
      color: $--select-input-color;
      line-height: 18px;
      font-size: $--select-input-font-size;

      &:hover {
        color: $--select-close-hover-color;
      }
    }

    @include e(tags1) {
      // position: absolute;
      // z-index: $--index-normal;
      // top: 50%;
      // transform: translateY(-50%);
      display: flex;
      line-height: normal;
      white-space: normal;
      align-items: center;
      flex-wrap: wrap;
      margin-left: -6px;
      & > span {
        display: contents;
      }
    }

    @include e(loading) {
      height: 33px;
      margin-top: 8px;
      z-index: -1;

      .zm-loading-spinner {
        .circular {
          height: 30px;
          width: 30px;
        }
      }
    }

    .zm-tag {
      box-sizing: border-box;
      border-color: transparent;
      margin: 3px 0 3px 6px;
      background-color: #f0f2f5;
      max-width: 200px;
      &__close.zm-icon-close {
        background-color: $--color-text-placeholder;
        right: -7px;
        top: 0;
        color: $--color-white;

        &:hover {
          background-color: $--color-text-secondary;
        }

        &::before {
          display: block;
          transform: translate(0, .5px);
        }
      }

      &.zm-tag-fix {
        margin: 3px 0 3px 6px;
      }

      &.is-hit {
        border-color: $--tag-info-color;
      }
    }
    @include m(small) {
      .zm-select-input1 {
        .zm-select__caret {
          font-size: $--font-size-small;
        }
      }
    }
    @include m(large) {
      .zm-select-input__inner1 {
        line-height: $--button-lg-height - 2;
        height: $--button-lg-height - 2;
      }
      .zm-select-span__inner1 {
        line-height: $--button-lg-height - 2;
        height: $--button-lg-height - 2;
      }
    }
  }

  .zm-select-fix {
    border: $--input-border;
    border-radius: $--input-border-radius;
    transition: $--border-transition-base;
    background-color: #fff;
    vertical-align: middle;
    box-sizing: border-box;

    &:hover,
    &.is-focus {
      border-color: $--input-focus-border;
      .zm-select__caret {
        color: $--color-border-primary;
      }
      [disabled] + .zm-select-toggle .zm-select__caret {
        color: inherit;
      }
    }
    &.is-disabled {
      cursor: not-allowed;
      background-color: $--disabled-fill-base;
      border-color: $--disabled-border-base;

      .zm-select-input__inner1 {
        color: $--color-text-disabled;
      }
      .zm-select-span__inner1 {
        color: $--color-text-disabled;
      }
    }
  }
</style>
